import React, { useMemo, useState, useContext } from 'react';
import { useUrlDateType } from '../Hooks/useUrlDateType';
import { useUrlSorting } from '../Hooks/useUrlSorting';
import { useUrlFilters, createFieldConfig, createDateConfig } from '../Hooks/useUrlFilters';
import { useViewFilters, usePaginationReset } from '../Hooks/useViewFilters';
import { useViewQuery } from '../Hooks/useViewQuery';
import { calculateRowCount } from '../utils/viewUtils';
import { useReusableTable } from '../common/TablePanel/tablePanel';
import { MaterialReactTable } from 'material-react-table';
import FiltersComponent from '../common/FiltersPanel/FIltersPanel';
import { TimezoneContext } from '../common/TimezoneContext/TimezoneContext';
import { Box, ThemeProvider, Typography } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RouterIcon from '@mui/icons-material/Router';
import FolderIcon from '@mui/icons-material/Folder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import FilterDropdown from '../common/FilterDropdown/FilterDropdown';
import './TestResultIngestionView.css';

const PREF_STORAGE_KEY = 'test_result_ingestion_filter_preferences';
const DEFAULT_FIELDS = {
  vin: [],
  mac_id: [],
  suite_name: [],
  status: [],
  reason_type: [],
};

const filterConfig = {
  dateFields: [createDateConfig('start_datetime', 'end_datetime', { startParam: 'startDate', endParam: 'endDate', allowEmpty: true })],
  debounceDelay: 100,
};

const TestResultIngestionApp = ({ activeTheme }) => {
  const [dateType, handleDateTypeChange] = useUrlDateType();
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 100 });
  const [sorting, setSorting] = useUrlSorting([{ id: 'datetime', desc: true }]);

  const mapping = { vin: 'vin', mac_id: 'mac_id', suite_name: 'suite_name', status: 'status', reason_type: 'reason_type' };

  const {
    singleValueFilters,
    debouncedSingleValueFilters,
    handleSingleFilterChange,
    handleSavePreferences,
    getOtherFilters,
    mergeField,
  } = useViewFilters({
    storageKey: PREF_STORAGE_KEY,
    defaultFields: DEFAULT_FIELDS,
    urlMapping: mapping,
    debounceDelay: 1000,
  });

  const { filters, debouncedFilters, handleDateRangeChange } = useUrlFilters(filterConfig);
  const { formatDateTime, createIsoStringWithTimezone, userTimezone, handleTimezoneChange } = useContext(TimezoneContext);

  const effectiveTimezone = userTimezone;

  const mergedFilters = useMemo(() => {
    return {
      ...debouncedFilters,
      vin: mergeField(debouncedSingleValueFilters.vin, debouncedFilters.vin),
      mac_id: mergeField(debouncedSingleValueFilters.mac_id, debouncedFilters.mac_id),
      suite_name: mergeField(debouncedSingleValueFilters.suite_name, debouncedFilters.suite_name),
      status: mergeField(debouncedSingleValueFilters.status, debouncedFilters.status),
      reason_type: mergeField(debouncedSingleValueFilters.reason_type, debouncedFilters.reason_type),
    };
  }, [debouncedFilters, debouncedSingleValueFilters, mergeField]);

  usePaginationReset(mergedFilters, dateType, setPagination);

  const testResultIngestionQuery = useViewQuery({
    queryKey: 'test_result_ingestion_data',
    mergedFilters,
    pagination,
    sorting,
    dateType,
    urlMapping: mapping,
    createIsoStringWithTimezone,
    endpoint: 'api/v1/test_result_ingestions/query',
  });

  const data = useMemo(() => testResultIngestionQuery.data?.results || [], [testResultIngestionQuery.data]);
  const rowCount = useMemo(() => calculateRowCount(testResultIngestionQuery.data, data, pagination), [testResultIngestionQuery.data, data, pagination]);

  const columns = useMemo(() => [
    { 
      accessorFn: (row) => row?.vin ?? '', 
      id: 'vin',
      header: 'VIN',
    },
    { 
      accessorFn: (row) => row?.mac_id ?? '', 
      id: 'mac_id',
      header: 'MAC ID',
    },
    { accessorKey: 'datetime', header: 'Date', Cell: ({ cell }) => formatDateTime(cell.getValue(), 'YYYY-MM-DD HH:mm:ss') },
    { accessorKey: 'suite_name', header: 'Suite Name' },
    {
      accessorKey: 'suite_source',
      header: 'Suite Source',
    },
    { accessorKey: 'start_time', header: 'Test Start Time', Cell: ({ cell }) => formatDateTime(cell.getValue(), 'YYYY-MM-DD HH:mm:ss') },
    { 
      accessorKey: 'elapsed', 
      header: 'Elapsed',
      Cell: ({ cell }) => {
        const seconds = cell.getValue();
        if (seconds == null || seconds === '') return 'N/A';
        
        const totalSeconds = Number(seconds);
        if (isNaN(totalSeconds)) return seconds;
        
        if (totalSeconds < 60) {
          return `${totalSeconds.toFixed(1)}s`;
        } else if (totalSeconds < 3600) {
          const minutes = Math.floor(totalSeconds / 60);
          const remainingSeconds = Math.floor(totalSeconds % 60);
          return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
        } else {
          const hours = Math.floor(totalSeconds / 3600);
          const remainingMinutes = Math.floor((totalSeconds % 3600) / 60);
          return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
        }
      }
    },
    { accessorKey: 'status', header: 'Status' },
    {
      accessorKey: 'failure_reasons',
      header: 'Failure Reasons',
      Cell: ({ cell }) => {
        let reasons = cell.getValue();
        
        if (!reasons) {
          return <Typography variant="body2" sx={{ color: 'text.secondary' }}>N/A</Typography>;
        }
        
        if (typeof reasons === 'string') {
          try {
            if (reasons.trim().startsWith('[') || reasons.trim().startsWith('{')) {
              reasons = JSON.parse(reasons);
            } else {
              return (
                <Typography
                  variant="body2"
                  sx={{
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    overflowWrap: 'break-word',
                  }}
                >
                  {reasons}
                </Typography>
              );
            }
          } catch (e) {
            return (
              <Typography
                variant="body2"
                sx={{
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  overflowWrap: 'break-word',
                }}
              >
                {reasons}
              </Typography>
            );
          }
        }
        
        if (!Array.isArray(reasons)) {
          reasons = [reasons];
        }
        
        if (reasons.length === 0) {
          return <Typography variant="body2" sx={{ color: 'text.secondary' }}>N/A</Typography>;
        }
        
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {reasons.map((reason, index) => {
              if (typeof reason === 'string') {
                return (
                  <Typography key={index} variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                    {reason}
                  </Typography>
                );
              }
              
              if (typeof reason === 'object' && reason !== null) {
                const reasonType = reason.reason_type || reason.type || 'Unknown';
                const reasonData = reason.data || reason.message || reason.description || JSON.stringify(reason);
                const reasonDate = reason.datetime ? formatDateTime(reason.datetime, 'YYYY-MM-DD HH:mm:ss') : '';
                
                return (
                  <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                      {reasonType}
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'normal', pl: 1 }}>
                      {reasonData}
                    </Typography>
                    {reasonDate && (
                      <Typography variant="caption" sx={{ color: 'text.secondary', pl: 1 }}>
                        {reasonDate}
                      </Typography>
                    )}
                  </Box>
                );
              }
              
              return (
                <Typography key={index} variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                  {String(reason)}
                </Typography>
              );
            })}
          </Box>
        );
      },
    },
  ], [formatDateTime]);

  const dropdowns = useMemo(() => (
    <>
      <Box sx={{ mt: 2 }}>
        <FilterDropdown
          field="vin"
          label="VIN"
          value={singleValueFilters.vin}
          onChange={(value) => handleSingleFilterChange('vin', value)}
          apiBaseUrl="/api/test_result_ingestions"
          multiple={true}
          icon={DirectionsCarIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('vin')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="mac_id"
          label="MAC ID"
          value={singleValueFilters.mac_id}
          onChange={(value) => handleSingleFilterChange('mac_id', value)}
          apiBaseUrl="/api/test_result_ingestions"
          multiple={true}
          icon={RouterIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('mac_id')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="suite_name"
          label="Suite Name"
          value={singleValueFilters.suite_name}
          onChange={(value) => handleSingleFilterChange('suite_name', value)}
          apiBaseUrl="/api/test_result_ingestions"
          multiple={true}
          icon={FolderIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('suite_name')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="status"
          label="Status"
          value={singleValueFilters.status}
          onChange={(value) => handleSingleFilterChange('status', value)}
          apiBaseUrl="/api/test_result_ingestions"
          multiple={true}
          icon={CheckCircleIcon}
          disableSearch={true}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('status')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="reason_type"
          label="Reason Type"
          value={singleValueFilters.reason_type}
          onChange={(value) => handleSingleFilterChange('reason_type', value)}
          apiBaseUrl="/api/test_result_ingestions"
          multiple={true}
          icon={InfoIcon}
          disableSearch={true}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('reason_type')}
          fieldMapping={mapping}
        />
      </Box>
    </>
  ), [
    singleValueFilters,
    handleSingleFilterChange,
    debouncedFilters,
    dateType,
    createIsoStringWithTimezone,
    getOtherFilters,
    mapping
  ]);

  const { table } = useReusableTable({
    columns,
    data,
    rowCount: rowCount,
    isLoading: testResultIngestionQuery.isFetching,
    pagination,
    sorting,
    onSortingChange: setSorting,
    manualPagination: true,
    onPaginationChange: setPagination,
    getRowId: row => row.id,
    enableRowSelection: false,
    enableRowActions: false,
    enableDefaultPreview: false,
    enableDefaultDownload: false,
    enableDefaultAddToCart: false,
    enableRowExpansion: false,
    enableCompareFiles: false,
    enableCSVExport: true,
    csvExportFilename: 'test-result-ingestion-export.csv',
    maxHeight: 'calc(100vh - 250px)',
    muiTableContainerProps: { sx: { maxHeight: 'calc(100vh - 250px)' } },
    enableRowVirtualization: true,
    paginationDisplayMode: 'default',
    muiPaginationProps: { 
      color: 'primary', 
      rowsPerPageOptions: [100, 200, 500, 1000], 
      shape: 'rounded', 
      variant: 'outlined',
      showFirstButton: false,
      showLastButton: false
    },
  });


  return (
    <ThemeProvider theme={activeTheme}>
      <Box className="test-result-ingestion-main-container">
        <Box>
          <FiltersComponent
            activeTheme={activeTheme}
            dateRangePicker={{ filters: filters || {}, handleDateRangeChange }}
            timezoneSelector={{ timezone: effectiveTimezone, userTimezone, handleTimezoneChange }}
            dateTypeSelector={{ dateType, handleDateTypeChange }}
            onSaveSearch={handleSavePreferences}
            storageKey={PREF_STORAGE_KEY}
            dropdowns={dropdowns}
          />
        </Box>
        <Box className="test-result-ingestion-content">
          <MaterialReactTable table={table} />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default TestResultIngestionApp;